import { PLUGIN_ALIASES, PLUGIN_REGISTRY } from "./gsapPluginRegistry";

let gsapCorePromise = null;
const loadedPluginPromises = {};
const registeredPluginNames = {};

function normalizePluginName(pluginName) {
    return String(pluginName).toLowerCase().replace(/[^a-z0-9]/g, "");
}

function readGsapCoreModule(module) {
    return module.default || module.gsap;
}

function loadGsapCore() {
    if (!gsapCorePromise) {
        gsapCorePromise = import("gsap")
            .then(readGsapCoreModule)
            .catch(function handleGsapCoreLoadFailure(error) {
                gsapCorePromise = null;
                throw error;
            });
    }
    return gsapCorePromise;
}

function resolveRequestedName(requestedName) {
    const normalizedKey = normalizePluginName(requestedName);
    const aliasTargetNames = PLUGIN_ALIASES[normalizedKey];
    if (aliasTargetNames) {
        return aliasTargetNames;
    }
    const entry = PLUGIN_REGISTRY[normalizedKey];
    if (entry) {
        return [entry.name];
    }
    return [];
}

function classifyRequestedName(classification, requestedName) {
    const resolvedNames = resolveRequestedName(requestedName);
    if (resolvedNames.length === 0) {
        return {
            knownNames: classification.knownNames,
            unknownNames: [...classification.unknownNames, requestedName],
        };
    }
    return {
        knownNames: [...classification.knownNames, ...resolvedNames],
        unknownNames: classification.unknownNames,
    };
}

function resolveRequestedNames(requestedNames) {
    return requestedNames.reduce(classifyRequestedName, { knownNames: [], unknownNames: [] });
}

function appendPluginNameWithDependencies(orderedNames, visitedNames, pluginName) {
    const entry = PLUGIN_REGISTRY[normalizePluginName(pluginName)];
    if (!entry || visitedNames[entry.name]) {
        return;
    }
    visitedNames[entry.name] = true;
    entry.requires.forEach(function appendRequiredName(requiredName) {
        appendPluginNameWithDependencies(orderedNames, visitedNames, requiredName);
    });
    orderedNames.push(entry.name);
}

function resolveOrderedPluginNames(requestedNames) {
    const orderedNames = [];
    const visitedNames = {};
    requestedNames.forEach(function appendRequestedName(requestedName) {
        appendPluginNameWithDependencies(orderedNames, visitedNames, requestedName);
    });
    return orderedNames;
}

function loadPlugin(pluginName) {
    if (!loadedPluginPromises[pluginName]) {
        loadedPluginPromises[pluginName] = PLUGIN_REGISTRY[normalizePluginName(pluginName)]
            .loader()
            .catch(function handlePluginLoadFailure(error) {
                delete loadedPluginPromises[pluginName];
                throw error;
            });
    }
    return loadedPluginPromises[pluginName];
}

function collectPluginClasses(orderedNames) {
    return Promise.all(orderedNames.map(loadPlugin));
}

function registerPluginOnce(gsap, pluginName, pluginClass) {
    if (registeredPluginNames[pluginName]) {
        return;
    }
    registeredPluginNames[pluginName] = true;
    gsap.registerPlugin(pluginClass);
}

function registerPluginsAndBuildObject(gsap, orderedNames, pluginClasses) {
    const plugins = {};
    orderedNames.forEach(function registerAndCollectPlugin(pluginName, pluginIndex) {
        registerPluginOnce(gsap, pluginName, pluginClasses[pluginIndex]);
        plugins[pluginName] = pluginClasses[pluginIndex];
    });
    return plugins;
}

function runUserCallback(callback, gsap, plugins) {
    if (typeof callback !== "function") {
        return;
    }
    try {
        callback(gsap, plugins);
    } catch (error) {
        console.error("loadGsap: error in user callback", error);
    }
}

function warnAboutUnknownNames(unknownNames) {
    if (unknownNames.length === 0) {
        return;
    }
    console.warn(`loadGsap: skipped unknown plugin name(s): ${unknownNames.join(", ")}`);
}

export default function loadGsap(pluginNames, callback) {
    const requestedNames = Array.isArray(pluginNames) ? pluginNames : [];
    const resolvedRequest = resolveRequestedNames(requestedNames);
    warnAboutUnknownNames(resolvedRequest.unknownNames);
    const orderedNames = resolveOrderedPluginNames(resolvedRequest.knownNames);
    return loadGsapCore()
        .then(function loadRequestedPlugins(gsap) {
            return collectPluginClasses(orderedNames).then(
                function finishPluginLoading(pluginClasses) {
                    return {
                        gsap,
                        plugins: registerPluginsAndBuildObject(gsap, orderedNames, pluginClasses),
                    };
                },
            );
        })
        .then(function invokeUserCallback(loadResult) {
            runUserCallback(callback, loadResult.gsap, loadResult.plugins);
            return loadResult;
        })
        .catch(function handleLoadGsapFailure(error) {
            console.error("loadGsap: failed to load GSAP module(s)", error);
            throw error;
        });
}
